import { useMotionValueEvent } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { SNAPSHOT_THROTTLE_MS } from "../investigation.constants";
import { IPlaybackSnapshot } from "../investigation.models";
import { sampleAt } from "../investigation.utils";
import { usePlaybackContext } from "./ride-investigation-playback.context";

/**
 * Throttled playback snapshot for readouts (timestamp, occupancy, current-station highlight).
 * The marker uses the raw motion value for smoothness; text/UI only needs a few updates per second,
 * so we sample at most every `throttleMs` to keep re-renders cheap.
 */
export const usePlaybackSnapshot = (
  throttleMs = SNAPSHOT_THROTTLE_MS,
): IPlaybackSnapshot | undefined => {
  const { model, playheadMs } = usePlaybackContext();
  const modelRef = useRef(model);
  modelRef.current = model;
  const lastRef = useRef(0);
  const [snapshot, setSnapshot] = useState<IPlaybackSnapshot | undefined>(() =>
    model ? sampleAt(model, playheadMs.get()) : undefined,
  );

  useEffect(() => {
    setSnapshot(model ? sampleAt(model, playheadMs.get()) : undefined);
  }, [model, playheadMs]);

  useMotionValueEvent(playheadMs, "change", (ms: number) => {
    const activeModel = modelRef.current;

    if (!activeModel) {
      return;
    }

    const now = performance.now();

    if (now - lastRef.current < throttleMs) {
      return;
    }

    lastRef.current = now;
    setSnapshot(sampleAt(activeModel, ms));
  });

  return snapshot;
};
